import React from 'react';
import { Badge, Alert, TableSkeleton, Button } from '@/components/ui';
import type { Organization } from '@/services/admin/AdminService';

interface OrganizationTableProps {
  organizations: Organization[];
  loading: boolean;
  error: string | null;
  onEdit: (org: Organization) => void;
  onDelete: (org: Organization) => void;
  onViewMembers: (org: Organization) => void;
}

const getTierVariant = (
  tier: string
): 'default' | 'primary' | 'success' | 'warning' | 'error' => {
  switch (tier) {
    case 'ENTERPRISE':
      return 'primary';
    case 'PROFESSIONAL':
      return 'success';
    case 'STARTER':
      return 'warning';
    default:
      return 'default';
  }
};

export const OrganizationTable: React.FC<OrganizationTableProps> = ({
  organizations,
  loading,
  error,
  onEdit,
  onDelete,
  onViewMembers,
}) => {
  if (loading) {
    return <TableSkeleton rows={5} columns={6} />;
  }

  if (error) {
    return (
      <Alert variant="error" className="mb-4">
        {error}
      </Alert>
    );
  }

  if (organizations.length === 0) {
    return (
      <Alert variant="info">
        No organizations found. Create one to get started.
      </Alert>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-secondary-200">
            <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
              Name
            </th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
              Slug
            </th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
              Tier
            </th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
              Limits
            </th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
              Status
            </th>
            <th className="text-left py-3 px-4 text-sm font-semibold text-secondary-700">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {organizations.map((org) => (
            <tr
              key={org.id}
              className="border-b border-secondary-100 hover:bg-secondary-50"
            >
              <td className="py-3 px-4">
                <div>
                  <span className="font-semibold text-secondary-900">
                    {org.name}
                  </span>
                  {org.description && (
                    <p className="text-sm text-secondary-500 truncate max-w-xs">
                      {org.description}
                    </p>
                  )}
                </div>
              </td>
              <td className="py-3 px-4">
                <span className="text-sm font-mono text-secondary-600">
                  {org.slug}
                </span>
              </td>
              <td className="py-3 px-4">
                <Badge variant={getTierVariant(org.subscription_tier)}>
                  {org.subscription_tier}
                </Badge>
              </td>
              <td className="py-3 px-4">
                <span className="text-sm text-secondary-600">
                  {org.max_users} users / {org.max_projects} projects
                </span>
              </td>
              <td className="py-3 px-4">
                <Badge variant={org.is_active ? 'success' : 'default'}>
                  {org.is_active ? 'Active' : 'Inactive'}
                </Badge>
              </td>
              <td className="py-3 px-4">
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewMembers(org)}
                  >
                    Members
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onEdit(org)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(org)}
                    className="text-error-600 hover:text-error-700"
                  >
                    Delete
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OrganizationTable;
